import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRenameQuestionKey } from "@/hooks/useScoringRules";

const QUESTION_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

interface RenameQuestionKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quizId: string;
  oldKey: string;
  affectedRulesCount: number;
  existingKeys: Set<string>;
}

export function RenameQuestionKeyDialog({
  open,
  onOpenChange,
  quizId,
  oldKey,
  affectedRulesCount,
  existingKeys,
}: RenameQuestionKeyDialogProps) {
  const [newKey, setNewKey] = useState(oldKey);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = useRenameQuestionKey(quizId);

  useEffect(() => {
    if (open) {
      setNewKey(oldKey);
      setSubmitError(null);
      mutation.reset();
    }
    // We deliberately depend only on open/oldKey — mutation.reset() is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oldKey]);

  const trimmedKey = newKey.trim();
  const isEmpty = trimmedKey.length === 0;
  const formatValid = QUESTION_KEY_PATTERN.test(trimmedKey);
  const conflict = existingKeys.has(trimmedKey) && trimmedKey !== oldKey;

  const validationHint = isEmpty
    ? null
    : !formatValid
      ? "Nur Kleinbuchstaben, Ziffern und Unterstriche. Start mit Buchstabe."
      : conflict
        ? "Key wird bereits im Quiz verwendet."
        : null;

  const submitDisabled =
    isEmpty || !formatValid || conflict || mutation.isPending;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitDisabled) return;
    setSubmitError(null);
    try {
      await mutation.mutateAsync({ oldKey, newKey: trimmedKey });
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Umbenennen fehlgeschlagen.",
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Frage-Key umbenennen</DialogTitle>
            <DialogDescription>
              Synchronisiert Scoring-Regeln auf einen geänderten Frage-Key im
              Quiz. Verändert die Scoring-Regeln für {affectedRulesCount}{" "}
              {affectedRulesCount === 1 ? "Eintrag" : "Einträge"}. Die
              Quiz-Konfiguration wird dabei nicht angetastet.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-1">
              <Label htmlFor="rename-current-key">Aktueller Key</Label>
              <Input
                id="rename-current-key"
                value={oldKey}
                readOnly
                disabled
                className="font-mono"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="rename-new-key">Neuer Key</Label>
              <Input
                id="rename-new-key"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                pattern="[a-z][a-z0-9_]*"
                minLength={1}
                maxLength={200}
                required
                className="font-mono"
                aria-invalid={validationHint !== null}
                aria-describedby={
                  validationHint ? "rename-new-key-hint" : undefined
                }
              />
              {validationHint && (
                <p
                  id="rename-new-key-hint"
                  className="text-xs text-destructive"
                >
                  {validationHint}
                </p>
              )}
            </div>

            {submitError && (
              <p className="text-sm text-destructive">{submitError}</p>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird umbenannt...
                </>
              ) : (
                "Umbenennen"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
